import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router";
import { Link, useHistory } from "react-router-dom";

import { Avatar, Button, Form, message, Row, Space, List, Transfer, Radio } from "antd";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { fonts } from "../../../styles";
import { clientUrls } from "misc/urls";
import { useApps, useCustomers, usePermissions, useUsers } from "misc/api/keycloakApi";
import { getQueryParameter } from "misc/helpfunctions";
import { TextField } from "misc/fields";
import { Card } from "misc/card";
import SideHeader from "navigation/SideHeader";
import CustomerAppPermissions from "admin/components/steps/CustomerAppPermissions";
import UserEmail from "admin/components/steps/UserEmail";
import CustomerName from "admin/components/steps/CustomerName";
import CustomerApps from "admin/components/steps/CustomerApps";
import UserFirstName from "admin/components/steps/UserFirstName";
import UserLastName from "admin/components/steps/UserLastName";
import CustomerRootUser from "admin/components/steps/CustomerRootUser";

const Title = styled(TextField)({
    fontSize: fonts.size16
});

const CustomerComponent = () => {
    const [t] = useTranslation();
    const [form] = Form.useForm();
    const { apps: allApps } = useApps({ suspense: true });
    const { customers, customersQueryState, updateCustomer, createCustomer, createCustomerMutation, updateCustomerMutation } = useCustomers({
        suspense: true
    });
    const { users } = useUsers({ suspense: true });
    const { permissions } = usePermissions({ suspense: true });
    const location = useLocation();
    const history = useHistory();
    const [configurationMode, setConfigurationMode] = useState(
        !customers.find(customer => customer.id === getQueryParameter(location, "customerId")) ? "new" : "existing"
    );
    const selectedApps = Form.useWatch("selectedApps", form);
    const customer = useMemo(() => customers.find(customer => customer.id === getQueryParameter(location, "customerId")), [customers, location]);

    const customerApps = useMemo(() => {
        const apps = customer?.permissions
            .filter(permission => permission.name.includes("permission_app_"))
            .filter(permission => allApps?.includes(permission.name.replace("permission_app_", "")))
            .map(permission => permission.name.replace("permission_app_", ""));
        return apps;
    }, [customer, selectedApps, allApps]);

    const baseSteps = useMemo(() => {
        return [
            {
                key: "customer-name",
                title: "t_name",
                icon: "Aa",
                content: <CustomerName customers={customers} form={form} name="name" value={customer?.name} />
            },
            {
                key: "customer-apps",
                title: "t_apps",
                icon: <FontAwesomeIcon icon={["fas", "grid-2"]} />,
                content: <CustomerApps form={form} name="selectedApps" value={customerApps ?? []} />
            },
            {
                key: `viisi-app-permissions`,
                title: `t_permissions_viisi`,
                icon: <FontAwesomeIcon icon={["fas", "lock"]} />,
                content: (
                    <CustomerAppPermissions
                        form={form}
                        name={`viisiAppPermissions`}
                        value={customer?.roles.filter(permission => permission.client === "viisi").map(permission => permission.id) ?? []}
                        client={"viisi"}
                    />
                ),
                visible: selectedApps?.includes("viisi")
            },
            {
                key: `analysisframework-app-permissions`,
                title: `t_permissions_analysisframework`,
                icon: <FontAwesomeIcon icon={["fas", "lock"]} />,
                content: (
                    <CustomerAppPermissions
                        form={form}
                        name={`analysisframeworkAppPermissions`}
                        value={customer?.roles.filter(permission => permission.client === "analysisframework").map(permission => permission.id) ?? []}
                        client={"analysisframework"}
                    />
                ),
                visible: selectedApps?.includes("analysisframework")
            },
            {
                key: "rootUser",
                title: "t_customer_root_user",
                icon: <FontAwesomeIcon icon={["fas", "users"]} />,
                content: (
                    <Space direction="vertical" size={30} style={{ width: "100%" }}>
                        <Radio.Group value={configurationMode} onChange={e => setConfigurationMode(e.target.value)} buttonStyle="solid">
                            <Radio.Button value="existing" disabled={!customer}>
                                <TextField value="t_customer_root_user_mode_select_existing" />
                            </Radio.Button>
                            <Radio.Button value="new">
                                <TextField value="t_customer_root_user_mode_create_new" />
                            </Radio.Button>
                        </Radio.Group>
                        {customer && configurationMode === "existing" && (
                            <CustomerRootUser
                                form={form}
                                label={t("t_users")}
                                name="rootUser"
                                value={customer?.members.find(member => member?.organizationRoles?.includes("org-root-user"))?.id}
                                members={customer?.members}
                                isLoading={customersQueryState.isLoading}
                            />
                        )}
                        {(!customer || configurationMode === "new") && (
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                <UserEmail label="t_user_email" users={users} form={form} name="rootUserEmail" />
                                <UserFirstName label="t_user_first_name" form={form} name="rootUserFirstName" />
                                <UserLastName label="t_user_last_name" form={form} name="rootUserLastName" />
                            </Space>
                        )}
                    </Space>
                ),
                visible: true
            }
        ];
    }, [customer, form, selectedApps, t, users, customersQueryState.isLoading, configurationMode]);

    const onClickSave = values => {
        const userManagementPermissionsIds = permissions
            .filter(permission => permission.name.includes("permission_app_usermanagement"))
            .map(permission => permission.id);
        const selectedAppsPermissions = values.selectedApps?.map(app => `permission_app_${app}`) || [];
        const appPermissionsIds = permissions
            .filter(permission => selectedAppsPermissions.includes(permission.name))
            .map(permission => permission.id);

        let rootUserId;
        let rootUser;
        if (customer && configurationMode === "existing") {
            rootUserId = users.find(user => user.id === values?.rootUser)?.id;
        } else {
            // when creating a new customer or adding new root user
            rootUser = {
                email: values?.rootUserEmail,
                firstName: values?.rootUserFirstName,
                lastName: values?.rootUserLastName
            };
        }

        const payload = {
            name: values?.name,
            permissions: [
                ...(appPermissionsIds || []),
                ...(values.viisiAppPermissions || []),
                ...(values.analysisframeworkAppPermissions || []),
                ...(userManagementPermissionsIds || [])
            ],
            ...(customer && { id: customer.id }),
            ...(configurationMode === "new" ? { rootUser } : { rootUserId })
        };
        const action = customer ? onEditCustomer : onCreateCustomer;
        action(payload);
    };

    const onCreateCustomer = customer => {
        createCustomer(customer, {
            onSuccess: () => message.success(t("t_customer_create_success")) && history.push(clientUrls.customers.baseUrl()),
            onError: error => {
                return message.error(error.error ? error.error : t(`t_http_error_${error.status}`));
            }
        });
    };

    const onEditCustomer = customer => {
        updateCustomer(customer, {
            onSuccess: () => message.success(t("t_customer_edit_success")) && history.push(clientUrls.customers.baseUrl()),
            onError: error => {
                return message.error(error.error ? error.error : t(`t_http_error_${error.status}`));
            }
        });
    };

    useEffect(() => {
        setConfigurationMode(!customer ? "new" : "existing");
    }, [customer]);

    return (
        <SideHeader>
            <Card
                height={800}
                stretchHeight
                title={customer ? "t_customer_edit_title" : "t_customer_create_title"}
                isLoading={customersQueryState.isLoading}
            >
                <List
                    split={false}
                    dataSource={baseSteps.filter(step => step.visible !== false)}
                    renderItem={item => (
                        <List.Item key={item.key}>
                            <Space direction="vertical" size={20} style={{ width: "100%" }}>
                                <Space size={12}>
                                    <Avatar size={40}>{item.icon}</Avatar>
                                    <Title value={item.title} />
                                </Space>
                                {item.content}
                            </Space>
                        </List.Item>
                    )}
                />

                <Row type="flex" justify="end">
                    <Form form={form} onFinish={onClickSave}>
                        <Form.Item>
                            <Space direction="horizontal" size={3}>
                                <Link to={clientUrls.customers.baseUrl()}>
                                    <Button>
                                        <TextField value="t_cancel" />
                                    </Button>
                                </Link>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={createCustomerMutation.isLoading || updateCustomerMutation.isLoading}
                                >
                                    <TextField inline value="t_save" />
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Row>
            </Card>
        </SideHeader>
    );
};
export default CustomerComponent;
